import {
  InteractionEvent,
  type PasswordVerificationPayload,
  SignInIdentifier,
  type VerificationCodeIdentifier,
} from '@logto/schemas';

import { type ContinueFlowInteractionEvent } from '@/types';

import api from '../api';

import { experienceApiRoutes, type VerificationResponse } from './const';
import {
  initInteraction,
  identifyUser,
  submitInteraction,
  updateInteractionEvent,
  updateProfile,
  identifyAndSubmitInteraction,
} from './interaction';

export {
  initInteraction,
  submitInteraction,
  identifyUser,
  identifyAndSubmitInteraction,
} from './interaction';

export * from './mfa';
export * from './social';
export * from './one-time-token';

/**
 * For sign-in flow user identity not found error handling use.
 *
 * If the identity is not found, but the verification record is verified,
 * allow the user registration with the verified identifier.
 *
 * Supported verification types:
 * - email verification code
 * - phone number verification code
 * - social sign-in
 * - SSO sign-in
 */
export const registerWithVerifiedIdentifier = async (verificationId: string) => {
  await updateInteractionEvent(InteractionEvent.Register);
  return identifyAndSubmitInteraction({ verificationId });
};

/**
 * Stage the verified register identifier (social/email/phone) but DON'T create the user yet.
 * The identifier is stored in the interaction session (uniqueness validated here).
 * The user is created together with submitInteraction on the extra-profile page,
 * so abandoning the flow midway leaves no orphan account behind.
 */
export const registerUserWithoutSubmit = async (
  verificationId: string,
  identifierType: SignInIdentifier.Email | SignInIdentifier.Phone | 'social'
) => {
  await updateInteractionEvent(InteractionEvent.Register);
  await updateProfile({ type: identifierType, verificationId });
};

/**
 * For sign-up flow user identifier already exists error handling user.
 *
 * If the identifier has been registered by an existing user,
 * allow the user to sign-in with the verified identifier directly.
 *
 * Supported verification types:
 * - email verification code
 * - phone number verification code
 */
export const signInWithVerifiedIdentifier = async (verificationId: string) => {
  await updateInteractionEvent(InteractionEvent.SignIn);
  return identifyAndSubmitInteraction({ verificationId });
};

// Password APIs
export const signInWithPasswordIdentifier = async (
  payload: PasswordVerificationPayload,
  captchaToken?: string
) => {
  await initInteraction(InteractionEvent.SignIn, captchaToken);

  const { verificationId } = await api
    .post(`${experienceApiRoutes.verification}/password`, {
      json: payload,
    })
    .json<VerificationResponse>();

  return identifyAndSubmitInteraction({ verificationId });
};

export const registerWithUsername = async (username: string, captchaToken?: string) => {
  await initInteraction(InteractionEvent.Register, captchaToken);

  return updateProfile({ type: SignInIdentifier.Username, value: username });
};

export const continueRegisterWithPassword = async (password: string) => {
  await updateProfile({ type: 'password', value: password });

  return identifyAndSubmitInteraction();
};

// Verification code APIs
type VerificationCodePayload = {
  identifier: VerificationCodeIdentifier;
  code: string;
  verificationId: string;
};

export const sendVerificationCode = async (
  interactionEvent: InteractionEvent,
  identifier: VerificationCodeIdentifier
) =>
  api
    .post(`${experienceApiRoutes.verification}/verification-code`, {
      json: {
        interactionEvent,
        identifier,
      },
    })
    .json<VerificationResponse>();

const verifyVerificationCode = async (json: VerificationCodePayload) =>
  api
    .post(`${experienceApiRoutes.verification}/verification-code/verify`, {
      json,
    })
    .json<VerificationResponse>();

export const identifyWithVerificationCode = async (json: VerificationCodePayload) => {
  const { verificationId } = await verifyVerificationCode(json);
  return identifyAndSubmitInteraction({ verificationId });
};

/**
 * Verify the code and stage the identifier but DON'T create the user yet.
 * Uniqueness is validated here (e.g. phone_already_in_use surfaces on the OTP page).
 * The user is created together with submitInteraction on the extra-profile page.
 * Note: Interaction should already be in Register state from sendVerificationCodeApi.
 */
export const registerWithVerificationCodeWithoutSubmit = async (json: VerificationCodePayload) => {
  const { verificationId } = await verifyVerificationCode(json);
  await updateProfile({ type: json.identifier.type, verificationId });
};

// Profile APIs

export const updateProfileWithVerificationCode = async (
  json: VerificationCodePayload,
  interactionEvent?: ContinueFlowInteractionEvent
) => {
  const { verificationId } = await verifyVerificationCode(json);

  const {
    identifier: { type },
  } = json;

  await updateProfile({
    type,
    verificationId,
  });

  if (interactionEvent === InteractionEvent.Register) {
    await identifyUser();
  }

  return submitInteraction();
};

/**
 * Update profile with verification code but DON'T submit interaction.
 * The data stays staged in the interaction session; for Register flows the user
 * is created together with submitInteraction on the extra-profile page.
 */
export const updateProfileWithVerificationCodeWithoutSubmit = async (
  json: VerificationCodePayload
) => {
  const { verificationId } = await verifyVerificationCode(json);

  const {
    identifier: { type },
  } = json;

  await updateProfile({
    type,
    verificationId,
  });
};

type UpdateProfilePayload =
  | {
    type: SignInIdentifier.Username | 'password';
    value: string;
  }
  | {
    type: 'extraProfile';
    values: Record<string, unknown>;
  };

export const fulfillProfile = async (
  payload: UpdateProfilePayload,
  interactionEvent: ContinueFlowInteractionEvent
) => {
  await updateProfile(payload);

  if (interactionEvent === InteractionEvent.Register) {
    await identifyUser();
  }

  return submitInteraction();
};

/**
 * Fulfill profile (e.g., set password) but DON'T submit interaction.
 * The data stays staged in the interaction session; for Register flows the user
 * is created together with submitInteraction on the extra-profile page.
 */
export const fulfillProfileWithoutSubmit = async (payload: UpdateProfilePayload) => {
  await updateProfile(payload);
};

export const resetPassword = async (password: string) => {
  await api.put(`${experienceApiRoutes.profile}/password`, {
    json: {
      password,
    },
  });

  return submitInteraction();
};

// Update profile and submit interaction
export const updateProfileAndSubmit = async (
  values: Record<string, unknown>,
  interactionEvent: ContinueFlowInteractionEvent = InteractionEvent.Register
) => {
  await updateProfile({ type: 'extraProfile', values });

  if (interactionEvent === InteractionEvent.Register) {
    // Create the user only now, atomically with submit. Identifier, password and
    // extra profile were all staged in the interaction session by the previous steps,
    // so an abandoned flow never leaves a half-registered user behind.
    await identifyUser();
  }

  return submitInteraction();
};
